import { Component } from "@theme/component";
import { isMobileBreakpoint } from "@theme/utilities";
import { morph } from "@theme/morph";

/**
 * A custom element that handles custom quick-add button functionality.
 * This component reuses the existing quick-add modal system but allows
 * targeting specific products from anywhere on the page.
 *
 * @extends {Component}
 */
export class CustomQuickAddComponent extends Component {
    #abortController = /** @type {AbortController | null} */ (null);
    #cachedProductHtml = /** @type {Document | null} */ (null);

    get productPageUrl() {
        return this.dataset.productUrl;
    }

    get productId() {
        return this.dataset.productId;
    }

    get productTitle() {
        return this.dataset.productTitle;
    }

    get showPaymentButtons() {
        return this.dataset.showPaymentButtons === "true";
    }

    get colorScheme() {
        return this.dataset.colorScheme;
    }

    connectedCallback() {
        super.connectedCallback();

        // Pre-fetch the product page content when the component is connected
        if (this.productPageUrl) {
            this.fetchProductPage(this.productPageUrl);
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        if (this.#abortController) {
            this.#abortController.abort();
        }
    }

    /**
     * Handles quick add button click
     * @param {Event} event - The click event
     */
    handleClick = async (event) => {
        event.preventDefault();

        if (!this.#cachedProductHtml && this.productPageUrl) {
            await this.fetchProductPage(this.productPageUrl);
        }

        if (this.#cachedProductHtml) {
            // Create a fresh copy of the cached HTML to avoid modifying the original
            const freshHtmlCopy = new DOMParser().parseFromString(
                this.#cachedProductHtml.documentElement.outerHTML,
                "text/html"
            );

            await this.updateQuickAddModal(freshHtmlCopy);
        }

        this.#openQuickAddModal();
    };

    /**
     * Opens the quick-add modal
     */
    #openQuickAddModal = () => {
        const dialogComponent = document.getElementById("quick-add-dialog");

        if (!dialogComponent) {
            console.warn(
                "Quick-add dialog not found. Make sure the quick-add-modal snippet is included in your theme.liquid"
            );
            return;
        }

        // Add custom class to the modal wrapper for styling control
        dialogComponent.classList.add("custom-quick-add-modal");
        console.log(
            "Custom Quick Add: Added custom-quick-add-modal class to modal wrapper"
        );

        const productId = this.dataset.productId;
        const productTitle = this.dataset.productTitle;
        const productUrl = this.dataset.productUrl;
        const showPaymentButtons = this.dataset.showPaymentButtons;
        const colorScheme = this.dataset.colorScheme;
        const inheritColorScheme = this.dataset.inheritColorScheme;
        const heading = this.dataset.heading;
        const productDescription = this.dataset.productDescription;
        const showQuantitySelector = this.dataset.showQuantitySelector;
        const addToCartText = this.dataset.addToCartText;
        const debugInherit = this.dataset.debugInherit;
        const debugScheme = this.dataset.debugScheme;
        const debugRawInherit = this.dataset.debugRawInherit;
        const debugRawScheme = this.dataset.debugRawScheme;

        console.log("Custom Quick Add: All data attributes:", {
            productId,
            productTitle,
            productUrl,
            showPaymentButtons,
            colorScheme,
            inheritColorScheme,
            heading,
            productDescription,
            showQuantitySelector,
            addToCartText,
            debugInherit,
            debugScheme,
            debugRawInherit,
            debugRawScheme,
        });

        // Apply color scheme to the modal if specified
        const shouldInherit = this.dataset.inheritColorScheme === "true";
        if (!shouldInherit && this.colorScheme) {
            console.log(
                "Custom Quick Add: Applying color scheme:",
                this.colorScheme
            );
            const innerDialog = dialogComponent.querySelector("dialog");
            if (innerDialog) {
                // Add custom class to inner dialog for styling control
                innerDialog.classList.add("custom-quick-add-dialog");

                // Remove existing color scheme classes
                innerDialog.classList.remove(
                    "color-scheme-1",
                    "color-scheme-2",
                    "color-scheme-3",
                    "color-scheme-4",
                    "color-scheme-5"
                );
                // Add the new color scheme class
                innerDialog.classList.add(`color-${this.colorScheme}`);
                console.log(
                    "Custom Quick Add: Applied color scheme class:",
                    `color-${this.colorScheme}`
                );
            }
        } else if (shouldInherit) {
            console.log(
                "Custom Quick Add: Using inherited color scheme (no changes made)"
            );
        } else {
            console.log(
                "Custom Quick Add: No color scheme specified, using default"
            );
        }

        // Apply custom heading if specified
        if (heading) {
            // Look for existing header or create one
            let modalHeader = dialogComponent.querySelector(
                ".quick-add-modal__header"
            );
            if (!modalHeader) {
                // Create header element at the top of the modal content
                const modalContent = dialogComponent.querySelector(
                    "#quick-add-modal-content"
                );
                if (modalContent) {
                    modalHeader = document.createElement("div");
                    modalHeader.className = "quick-add-modal__header";
                    if (modalHeader instanceof HTMLElement) {
                        modalHeader.style.cssText =
                            "grid-column: 1 / -1; padding: var(--padding-lg); text-align: center; border-bottom: 1px solid var(--color-border);";
                    }
                    modalContent.insertBefore(
                        modalHeader,
                        modalContent.firstChild
                    );
                }
            }
            if (modalHeader) {
                modalHeader.innerHTML = heading;
                console.log(
                    "Custom Quick Add: Applied custom heading:",
                    heading
                );
            }
        }

        // Apply custom product description if specified
        if (productDescription) {
            const productDetails =
                dialogComponent.querySelector(".product-details");
            if (productDetails) {
                const viewProductTitle = productDetails.querySelector(
                    ".view-product-title"
                );
                if (viewProductTitle) {
                    // Insert description after the product title
                    const descriptionDiv = document.createElement("div");
                    descriptionDiv.className = "product-description";
                    descriptionDiv.style.cssText =
                        "margin-top: var(--margin-sm); margin-bottom: var(--margin-md);";
                    descriptionDiv.innerHTML = productDescription;
                    viewProductTitle.insertAdjacentElement(
                        "afterend",
                        descriptionDiv
                    );
                    console.log(
                        "Custom Quick Add: Applied custom product description:",
                        productDescription
                    );
                }
            }
        }

        // Control quantity selector visibility
        if (showQuantitySelector === "false") {
            console.log(
                "Custom Quick Add: Attempting to hide quantity selector..."
            );
            const quantitySelector = dialogComponent.querySelector(
                "quantity-selector-component"
            );
            console.log(
                "Custom Quick Add: Found quantity selector element:",
                quantitySelector
            );
            if (quantitySelector && quantitySelector instanceof HTMLElement) {
                console.log(
                    "Custom Quick Add: Quantity selector before hiding:",
                    quantitySelector.style.display
                );
                quantitySelector.style.display = "none";
                console.log(
                    "Custom Quick Add: Quantity selector after hiding:",
                    quantitySelector.style.display
                );
                console.log("Custom Quick Add: Hidden quantity selector");
            } else {
                console.log(
                    "Custom Quick Add: Could not find quantity selector element or it's not an HTMLElement"
                );
            }
        } else {
            console.log(
                "Custom Quick Add: Quantity selector should be visible (showQuantitySelector =",
                showQuantitySelector,
                ")"
            );
        }

        // Customize add to cart button text
        if (
            addToCartText &&
            addToCartText.trim() !== "" &&
            addToCartText !== "Add to Cart"
        ) {
            console.log(
                "Custom Quick Add: Attempting to change add to cart button text..."
            );
            console.log(
                "Custom Quick Add: addToCartText value:",
                addToCartText
            );
            console.log(
                "Custom Quick Add: addToCartText type:",
                typeof addToCartText
            );
            console.log(
                "Custom Quick Add: addToCartText length:",
                addToCartText.length
            );

            const addToCartButton = dialogComponent.querySelector(
                'button[type="submit"]'
            );
            console.log(
                "Custom Quick Add: Found add to cart button:",
                addToCartButton
            );
            if (addToCartButton) {
                console.log(
                    "Custom Quick Add: Button text before change:",
                    addToCartButton.textContent
                );
                console.log(
                    "Custom Quick Add: Button HTML before change:",
                    addToCartButton.outerHTML
                );
                addToCartButton.textContent = addToCartText;
                console.log(
                    "Custom Quick Add: Button text after change:",
                    addToCartButton.textContent
                );
                console.log(
                    "Custom Quick Add: Button HTML after change:",
                    addToCartButton.outerHTML
                );
                console.log(
                    "Custom Quick Add: Changed add to cart button text to:",
                    addToCartText
                );
            } else {
                console.log(
                    "Custom Quick Add: Could not find add to cart button"
                );
                // Try to find any button in the modal for debugging
                const allButtons = dialogComponent.querySelectorAll("button");
                console.log(
                    "Custom Quick Add: All buttons found in modal:",
                    allButtons
                );
                allButtons.forEach((btn, index) => {
                    console.log(`Custom Quick Add: Button ${index}:`, {
                        type: btn.type,
                        text: btn.textContent,
                        class: btn.className,
                        html: btn.outerHTML,
                    });
                });
            }
        } else {
            console.log(
                "Custom Quick Add: Add to cart button text should remain default (addToCartText =",
                addToCartText,
                ")"
            );
            console.log(
                "Custom Quick Add: addToCartText === 'Add to Cart':",
                addToCartText === "Add to Cart"
            );
            console.log(
                "Custom Quick Add: addToCartText is empty or whitespace:",
                !addToCartText || addToCartText.trim() === ""
            );
            console.log("Custom Quick Add: Using theme default button text");
        }

        // Check if the dialog component has the showDialog method
        if (typeof dialogComponent.showDialog === "function") {
            dialogComponent.showDialog();
        } else {
            console.warn(
                "Quick-add dialog component is not a valid dialog component"
            );
        }
    };

    /**
     * Fetches the product page content
     * @param {string} productPageUrl - The URL of the product page to fetch
     * @returns {Promise<void>}
     * @throws {Error} If the fetch request fails or returns a non-200 response
     */
    async fetchProductPage(productPageUrl) {
        if (!productPageUrl) return;

        // We use this to abort the previous fetch request if it's still pending.
        this.#abortController?.abort();
        this.#abortController = new AbortController();

        try {
            const response = await fetch(productPageUrl, {
                signal: this.#abortController.signal,
            });

            if (!response.ok) {
                throw new Error(
                    `Failed to fetch product page: HTTP error ${response.status}`
                );
            }

            const responseText = await response.text();
            const html = new DOMParser().parseFromString(
                responseText,
                "text/html"
            );

            // Store the HTML for later use
            this.#cachedProductHtml = html;
        } catch (error) {
            if (error.name === "AbortError") {
                return;
            } else {
                console.error("Error fetching product page:", error);
            }
        } finally {
            this.#abortController = null;
        }
    }

    /**
     * Updates the quick-add modal content
     * @param {Document} newHtml - The new HTML.
     */
    async updateQuickAddModal(newHtml) {
        const productGrid = newHtml.querySelector(
            "[data-product-grid-content]"
        );
        const modalContent = document.getElementById("quick-add-modal-content");

        console.log("Custom Quick Add: Fetched HTML:", newHtml);
        console.log("Custom Quick Add: Product grid content:", productGrid);
        console.log("Custom Quick Add: Modal content element:", modalContent);

        if (!productGrid || !modalContent) {
            console.warn(
                "Required elements not found for quick-add modal update"
            );
            return;
        }

        // Debug: Check what's in the product grid content
        console.log(
            "Custom Quick Add: Product grid HTML:",
            productGrid.outerHTML
        );

        // Look for payment buttons in the fetched content
        const paymentButtonsInFetched = productGrid.querySelectorAll(
            'shopify-accelerated-checkout, .accelerated-checkout-block, [data-shopify="payment-button"], .shopify-payment-button, [data-testid="shopify-payment-button"]'
        );
        console.log(
            "Custom Quick Add: Payment buttons found in fetched content:",
            paymentButtonsInFetched
        );

        // Clone the original structure but modify the form behavior
        const clonedContent = productGrid.cloneNode(true);

        // Modify the product form to go directly to checkout
        this.#modifyProductFormForCheckout(clonedContent);

        // Use morph if available, otherwise fall back to simple replacement
        if (typeof morph === "function") {
            morph(modalContent, clonedContent);
        } else {
            modalContent.innerHTML = "";
            modalContent.appendChild(clonedContent);
        }
    }

    #modifyProductFormForCheckout(clonedContent) {
        const productForm = clonedContent.querySelector(
            "product-form-component"
        );
        if (!productForm) return;

        // Remove the on:submit attribute to prevent JavaScript interception
        productForm.removeAttribute("on:submit");

        // Find the form element within the product form component
        const form = productForm.querySelector("form");
        if (!form) return;

        // Modify the form action to go directly to checkout
        form.action = "/cart/add";
        form.method = "POST";

        // Add a hidden input to redirect to checkout after adding to cart
        const redirectInput = document.createElement("input");
        redirectInput.type = "hidden";
        redirectInput.name = "return_to";
        redirectInput.value = "/checkout";
        form.appendChild(redirectInput);

        // Handle payment buttons based on the setting
        console.log(
            "Custom Quick Add: showPaymentButtons setting:",
            this.showPaymentButtons
        );

        if (!this.showPaymentButtons) {
            console.log(
                "Custom Quick Add: Hiding payment buttons as requested"
            );
            // Hide accelerated checkout buttons
            const acceleratedCheckoutButtons = productForm.querySelectorAll(
                'shopify-accelerated-checkout, .accelerated-checkout-block, [data-shopify="payment-button"], .shopify-payment-button, [data-testid="shopify-payment-button"]'
            );
            console.log(
                "Custom Quick Add: Found payment buttons to hide:",
                acceleratedCheckoutButtons
            );
            acceleratedCheckoutButtons.forEach((button) => {
                button.style.display = "none";
                console.log("Custom Quick Add: Hidden payment button:", button);
            });
        } else {
            console.log("Custom Quick Add: Payment buttons should be visible");
            // Check if payment buttons exist
            const existingPaymentButtons = productForm.querySelectorAll(
                'shopify-accelerated-checkout, .accelerated-checkout-block, [data-shopify="payment-button"], .shopify-payment-button, [data-testid="shopify-payment-button"]'
            );
            console.log(
                "Custom Quick Add: Existing payment buttons found:",
                existingPaymentButtons
            );

            // If no payment buttons exist, we might need to add them
            if (existingPaymentButtons.length === 0) {
                console.log(
                    "Custom Quick Add: No payment buttons found, may need to add them"
                );
            }
        }

        // Remove any existing submit event listeners and add our own
        const submitButton = form.querySelector(
            'button[type="submit"], input[type="submit"]'
        );
        if (submitButton) {
            // Change button text to indicate it goes to checkout
            const buttonText = submitButton.querySelector(
                ".button__text, .add-to-cart-text"
            );
            if (buttonText) {
                buttonText.textContent = "Add to Cart";
            }

            // Remove any existing click handlers
            submitButton.replaceWith(submitButton.cloneNode(true));
        }
    }
}

// Register the custom element
if (!customElements.get("custom-quick-add-component")) {
    customElements.define(
        "custom-quick-add-component",
        CustomQuickAddComponent
    );
}
